import type {
  ChainId,
  DiscoverResponse,
  HolderHistoryPoint,
  IndexStatus,
  MapData,
  SearchResult,
  TokenMetadata,
  WatchlistEntry,
} from '@pleco/shared';

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';
const BASE = API_BASE;
const TOKEN_KEY = 'pleco_token';
const EMAIL_KEY = 'pleco_email';

export const getToken = () => localStorage.getItem(TOKEN_KEY) ?? '';
export const getEmail = () => localStorage.getItem(EMAIL_KEY) ?? '';
export const setAuth = (token: string, email: string) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(EMAIL_KEY, email);
};
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
};

const unauthorizedCallbacks = new Set<() => void>();
export const onUnauthorized = (fn: () => void) => {
  unauthorizedCallbacks.add(fn);
  return () => unauthorizedCallbacks.delete(fn);
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  // Only declare a JSON body when there is one: Fastify rejects an empty body
  // sent with `content-type: application/json` (affects body-less POSTs).
  const typeHeader: Record<string, string> = init?.body !== undefined ? { 'content-type': 'application/json' } : {};
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...typeHeader, ...authHeader, ...((init?.headers as Record<string, string>) ?? {}) },
  });
  if (res.status === 401 && !path.startsWith('/auth/')) {
    clearAuth();
    unauthorizedCallbacks.forEach((fn) => fn());
    throw new ApiError('Authentication required', 401);
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* keep default */
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

export interface MapQuery {
  limit?: number;
  timestamp?: number;
  magic?: boolean;
  time?: boolean;
  contracts?: boolean;
  minEdge?: number;
  extra?: string[];
  refresh?: boolean;
  wait?: boolean;
  /** Live view: serve the cached map now, rebuild if older than this (s). */
  maxAge?: number;
}

const qs = (q: MapQuery): string => {
  const p = new URLSearchParams();
  if (q.limit) p.set('limit', String(q.limit));
  if (q.timestamp) p.set('timestamp', String(q.timestamp));
  if (q.magic !== undefined) p.set('magic', q.magic ? '1' : '0');
  if (q.time !== undefined) p.set('time', q.time ? '1' : '0');
  if (q.contracts !== undefined) p.set('contracts', q.contracts ? '1' : '0');
  if (q.minEdge) p.set('minEdge', String(q.minEdge));
  if (q.extra?.length) p.set('extra', q.extra.join(','));
  if (q.refresh) p.set('refresh', '1');
  if (q.wait) p.set('wait', '1');
  if (q.maxAge) p.set('maxAge', String(q.maxAge));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const api = {
  login: (email: string, password: string) =>
    req<{ token: string; email: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => req<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  me: () => req<{ email: string }>('/auth/me'),

  discover: () => req<DiscoverResponse>('/v1/discover'),

  health: () => req<{ ok: boolean; features: Record<string, boolean> }>('/health'),

  chains: () =>
    req<{
      default?: ChainId;
      chains: {
        id: ChainId;
        label: string;
        symbol: string;
        color: string;
        family: string;
        capabilities: { timeTravel: boolean; fundingTraces: boolean; fullHolderScan: boolean };
      }[];
    }>('/v1/chains'),

  demos: () =>
    req<{ enabled: boolean; scenarios: { address: string; chain: ChainId; name: string; symbol: string; description: string }[] }>(
      '/v1/demo',
    ),

  search: (q: string) => req<{ results: SearchResult[] }>(`/v1/search?q=${encodeURIComponent(q)}`),

  token: (chain: ChainId, address: string) =>
    req<{ token: TokenMetadata; range: { min: number; max: number } | null; status: IndexStatus }>(
      `/v1/tokens/${chain}/${encodeURIComponent(address)}`,
    ),

  map: (chain: ChainId, address: string, q: MapQuery = {}) =>
    req<MapData>(`/v1/maps/${chain}/${encodeURIComponent(address)}${qs(q)}`),

  status: (chain: ChainId, address: string) =>
    req<IndexStatus>(`/v1/maps/${chain}/${encodeURIComponent(address)}/status`),

  history: (chain: ChainId, address: string) =>
    req<{ points: HolderHistoryPoint[]; range: { min: number; max: number } | null }>(
      `/v1/maps/${chain}/${encodeURIComponent(address)}/history`,
    ),

  transfers: (chain: ChainId, token: string, address: string, peer?: string) =>
    req<{
      transfers: {
        hash: string;
        from: string;
        to: string;
        amount: number;
        timestamp: number;
        blockNumber: number;
        direction: 'in' | 'out';
      }[];
    }>(
      `/v1/transfers/${chain}/${encodeURIComponent(token)}/${encodeURIComponent(address)}${
        peer ? `?peer=${encodeURIComponent(peer)}` : ''
      }`,
    ),

  saveSnapshot: (chain: ChainId, address: string, title?: string) =>
    req<{ id: string }>(`/v1/snapshots/${chain}/${encodeURIComponent(address)}`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    }),

  snapshot: (id: string) => req<{ data: MapData }>(`/v1/snapshots/${id}`),

  watchlist: () => req<{ entries: WatchlistEntry[] }>('/v1/watchlist'),

  addWatch: (body: {
    chain: ChainId;
    address: string;
    symbol?: string;
    note?: string;
    alertScoreBelow?: number;
    alertClusterAbove?: number;
  }) => req<WatchlistEntry>('/v1/watchlist', { method: 'POST', body: JSON.stringify(body) }),

  patchWatch: (id: string, body: { note?: string | null; alertScoreBelow?: number | null; alertClusterAbove?: number | null }) =>
    req<WatchlistEntry>(`/v1/watchlist/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  removeWatch: (id: string) => req<{ ok: boolean }>(`/v1/watchlist/${id}`, { method: 'DELETE' }),

  authConfig: () => req<{ authEnabled: boolean }>('/auth/config'),

  alerts: () =>
    req<{
      events: AlertEvent[];
      channels: string[];
      intervalSeconds: number;
      readings: Record<string, { score?: number; cluster?: number; checkedAt?: number }>;
    }>('/v1/alerts'),

  runAlerts: (id?: string) =>
    req<{ results: { watchId: string; symbol: string; score: number; largestCluster: number; fired: AlertEvent[]; error?: string }[] }>(
      `/v1/alerts/run${id ? `?id=${encodeURIComponent(id)}` : ''}`,
      { method: 'POST' },
    ),

  testAlert: () => req<{ configured: string[]; delivered: string[] }>('/v1/alerts/test', { method: 'POST' }),

  compare: (tokens: { chain: ChainId; address: string }[]) =>
    req<{ rows: CompareRow[]; best?: string; worst?: string }>(
      `/v1/compare?tokens=${encodeURIComponent(tokens.map((t) => `${t.chain}:${t.address}`).join(','))}`,
    ),
};

export interface AlertEvent {
  id: number;
  watchId: string;
  chain: ChainId;
  address: string;
  symbol?: string;
  kind: 'score_below' | 'cluster_above' | 'score_drop' | 'cluster_growth';
  message: string;
  value?: number;
  threshold?: number;
  delivered: string[];
  createdAt: number;
}

export interface CompareRow {
  chain: ChainId;
  address: string;
  ok: boolean;
  error?: string;
  symbol?: string;
  name?: string;
  logoUrl?: string;
  priceUsd?: number;
  marketCapUsd?: number;
  liquidityUsd?: number;
  holders?: number;
  score?: number;
  grade?: string;
  risk?: number;
  riskLevel?: string;
  topFlags?: string[];
  clusters?: number;
  largestCluster?: number;
  clustered?: number;
  top10?: number;
  top10Adjusted?: number;
  cex?: number;
  dex?: number;
  freshWallets?: number;
  bundles?: number;
  snipers?: number;
  effectiveEntities?: number;
  gini?: number;
  partial?: boolean;
}
